// hooks/useAzureSpeech.js
// 'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk'

const {
    AudioConfig,
    SpeechConfig,
    SpeechRecognizer,
    ResultReason
} = SpeechSDK

// Disable web workers to avoid CSP issues with data: URLs
SpeechSDK.Recognizer.enableTelemetry(false)

export function useAzureSpeech() {
    const recognizerRef = useRef(null)
    const audioConfigRef = useRef(null)
    const resultsRef = useRef([])
    const countdownTimerRef = useRef(null)
    const stopRequestedRef = useRef(false)

    const [isListening, setIsListening] = useState(false)
    const [transcript, setTranscript] = useState('')
    const [lastError, setLastError] = useState(null)
    const [countdown, setCountdown] = useState(0)

    const cleanupRecognizer = useCallback(() => {
        try {
            if (recognizerRef.current) {
                try {
                    recognizerRef.current.stopContinuousRecognitionAsync(
                        () => {
                            try { recognizerRef.current?.close() } catch (ignored) {}
                            recognizerRef.current = null
                        },
                        (err) => {
                            console.warn('stopContinuousRecognitionAsync error', err)
                            try { recognizerRef.current?.close() } catch (ignored) {}
                            recognizerRef.current = null
                        }
                    )
                } catch (e) {
                    try { recognizerRef.current?.close() } catch (ignored) {}
                    recognizerRef.current = null
                }
            }
        } catch (e) {
            console.warn('cleanupRecognizer error', e)
            recognizerRef.current = null
        }
        // Close audio config to release microphone resource
        if (audioConfigRef.current) {
            try { audioConfigRef.current.close() } catch (ignored) {}
            audioConfigRef.current = null
        }
        setIsListening(false)
        if (countdownTimerRef.current) {
            window.clearInterval(countdownTimerRef.current)
            countdownTimerRef.current = null
        }
    }, [])

    useEffect(() => {
        return () => {
            cleanupRecognizer()
        }
    }, [cleanupRecognizer])

    const stopListening = useCallback(() => {
        stopRequestedRef.current = true
        cleanupRecognizer()
    }, [cleanupRecognizer])

    const startListening = useCallback(async (opts) => {
        setLastError(null)
        stopRequestedRef.current = false

        if (isListening) {
            console.warn('Speech recognizer already running')
            return
        }

        const key = process.env.REACT_APP_VOICE_PUBLIC_SPEECH_KEY
        // const key = "5Qp5KPO112A8X9saksm2qmzY6qOCrSHGeqHcyny6CCwyvCtiFC3PJQQJ99BGACfhMk5XJ3w3AAAYACOGBied"
        const region = process.env.REACT_APP_VOICE_PUBLIC_SPEECH_REGION
        // const region = "swedencentral"
        if (!key || !region) {
            setLastError('Speech recognition not configured (missing NEXT_PUBLIC keys).')
            return
        }

        resultsRef.current = []
        setTranscript('')
        setIsListening(true)

        const timeoutSeconds = opts?.timeoutSeconds ?? 15
        const language = opts?.language ?? 'en-US'
        setCountdown(timeoutSeconds)

        try {
            const speechConfig = SpeechConfig.fromSubscription(key, region)
            speechConfig.speechRecognitionLanguage = language
            speechConfig.enableDictation()

            // Clean up any existing audio config before creating new one
            if (audioConfigRef.current) {
                try { audioConfigRef.current.close() } catch (ignored) {}
            }
            const audioConfig = AudioConfig.fromDefaultMicrophoneInput()
            audioConfigRef.current = audioConfig
            const recognizer = new SpeechRecognizer(speechConfig, audioConfig)
            recognizerRef.current = recognizer

            recognizer.recognized = (_, e) => {
                if (e.result.reason === ResultReason.RecognizedSpeech && e.result.text.trim()) {
                    const part = e.result.text.trim()
                    resultsRef.current.push(part)
                    const full = resultsRef.current.join(' ')
                    setTranscript(full)
                } else if (e.result.reason === ResultReason.NoMatch) {
                    // no match: ignore or notify
                    // Sorry, I didn’t catch that. Please try speaking again.
                }
            }

            recognizer.recognizing = (_, e) => {
                if (e.result && e.result.text) {
                    const interim = [...resultsRef.current, e.result.text.trim()].join(' ')
                    setTranscript(interim)
                }
            }

            recognizer.canceled = (_, e) => {
                console.warn('recognizer canceled', e)
                if (e.errorDetails) {
                    setLastError(e.errorDetails)
                } else {
                    setLastError('Recognition canceled.')
                }
                cleanupRecognizer()
            }

            recognizer.sessionStopped = () => {
                cleanupRecognizer()
            }

            let remaining = timeoutSeconds
            setCountdown(remaining)
            if (countdownTimerRef.current) {
                window.clearInterval(countdownTimerRef.current)
            }
            countdownTimerRef.current = window.setInterval(() => {
                remaining -= 1
                setCountdown(remaining)
                if (remaining <= 0) {
                    if (countdownTimerRef.current) {
                        window.clearInterval(countdownTimerRef.current)
                        countdownTimerRef.current = null
                    }
                }
            }, 1000)

            recognizer.startContinuousRecognitionAsync(
                () => {
                    window.setTimeout(() => {
                        if (stopRequestedRef.current) return
                        if (recognizerRef.current) {
                            try {
                                recognizerRef.current.stopContinuousRecognitionAsync(
                                    () => {},
                                    () => {}
                                )
                            } catch (e) {
                                cleanupRecognizer()
                            }
                        }
                    }, timeoutSeconds * 1000)
                },
                (err) => {
                    console.error('Failed to start recognition', err)
                    setLastError('Failed to start recognition: ' + String(err))
                    cleanupRecognizer()
                }
            )
        } catch (err) {
            console.error('startListening error', err)
            setLastError('Speech recognition setup failed.')
            cleanupRecognizer()
        }
    }, [cleanupRecognizer, isListening])

    return {
        startListening,
        stopListening,
        isListening,
        transcript,
        lastError,
        // isMatched,
        countdown
    }
}
